import React, { useState } from 'react';
import { getTenant, formatCurrencyAmount } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { AlertCircleIcon, ArrowLeftIcon, CheckCircleIcon, XIcon, CalendarIcon } from 'lucide-react';
interface BulkPaymentPreviewProps {
  data: any[];
  onBack: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
}
export function BulkPaymentPreview({
  data,
  onBack,
  onConfirm,
  isSubmitting
}: BulkPaymentPreviewProps) {
  const {
    t
  } = useTranslation('payments');
  const [selectedRows, setSelectedRows] = useState<number[]>([...Array(data.length).keys()]);
  const toggleRow = (index: number) => {
    if (selectedRows.includes(index)) {
      setSelectedRows(selectedRows.filter(i => i !== index));
    } else {
      setSelectedRows([...selectedRows, index]);
    }
  };
  const toggleAllRows = () => {
    if (selectedRows.length === data.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows([...Array(data.length).keys()]);
    }
  };
  const hasErrors = data.some(row => {
    return !row.customerName || !row.email || !row.amount || !row.description || isNaN(parseFloat(row.amount)) || parseFloat(row.amount) <= 0;
  });
  // Calculate totals
  const selectedData = data.filter((_, index) => selectedRows.includes(index));
  const totalAmount = selectedData.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);
  const currency = data[0]?.currency || getTenant().currency.code;
  return <div className="space-y-6" data-id="element-8006">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-8007">
        <div className="flex justify-between items-center mb-6" data-id="element-8008">
          <h3 className="text-lg font-medium text-gray-800" data-id="element-8009">
            {t('components.BulkPaymentPreview.preview_batch')}
          </h3>
        </div>
        {hasErrors && <div className="p-4 bg-red-50 text-red-700 rounded-md flex items-start mb-6" data-id="element-8010">
            <AlertCircleIcon size={18} className="mt-0.5 mr-3 flex-shrink-0" data-id="element-8011" />
            <div data-id="element-8012">
              <p className="text-sm font-medium" data-id="element-8013">
                {t('components.BulkPaymentPreview.some_rows_have_errors')}
              </p>
              <p className="text-sm mt-1" data-id="element-8014">
                {t('components.BulkPaymentPreview.please_fix_the_issues_highlighted_in')}
              </p>
            </div>
          </div>}
        <div className="border border-gray-200 rounded-md overflow-hidden mb-6" data-id="element-8015">
          <div className="max-h-[400px] overflow-auto" data-id="element-8016">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-8017">
              <thead className="bg-gray-50 sticky top-0" data-id="element-8018">
                <tr data-id="element-8019">
                  <th className="px-4 py-3 text-left" data-id="element-8020">
                    <div className="flex items-center" data-id="element-8021">
                      <input type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" checked={selectedRows.length === data.length} onChange={toggleAllRows} data-id="element-8022" />
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8023">
                    {t('components.BulkPaymentPreview.customer')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8024">
                    {t('components.BulkPaymentPreview.contact')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8025">
                    {t('components.BulkPaymentPreview.amount')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8026">
                    {t('components.BulkPaymentPreview.description')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8027">
                    {t('components.BulkPaymentPreview.reference')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-8028">
                {data.map((row, index) => {
                const hasError = !row.customerName || !row.email || !row.amount || !row.description || isNaN(parseFloat(row.amount)) || parseFloat(row.amount) <= 0;
                return <tr key={index} className={hasError ? 'bg-red-50' : ''} data-id="element-8029">
                      <td className="px-4 py-3" data-id="element-8030">
                        <input type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" checked={selectedRows.includes(index)} onChange={() => toggleRow(index)} disabled={hasError} data-id="element-8031" />
                      </td>
                      <td className={`px-4 py-3 text-sm ${!row.customerName ? 'text-red-600' : 'text-gray-900'}`} data-id="element-8032">
                        {row.customerName || 'Missing name'}
                      </td>
                      <td className="px-4 py-3 text-sm" data-id="element-8033">
                        <div className={!row.email ? 'text-red-600' : 'text-gray-900'} data-id="element-8034">
                          {row.email || 'Missing email'}
                        </div>
                        {row.phone && <div className="text-gray-500 text-xs mt-1" data-id="element-8035">
                            {row.phone}
                          </div>}
                      </td>
                      <td className={`px-4 py-3 text-sm font-medium ${!row.amount || isNaN(parseFloat(row.amount)) || parseFloat(row.amount) <= 0 ? 'text-red-600' : 'text-gray-900'}`} data-id="element-8036">
                        {!row.amount || isNaN(parseFloat(row.amount)) ? 'Invalid amount' : parseFloat(row.amount) <= 0 ? 'Amount must be > 0' : formatCurrencyAmount(parseFloat(row.amount), {
                      currency: row.currency || currency,
                      fractionDigits: 2
                    })}
                      </td>
                      <td className={`px-4 py-3 text-sm ${!row.description ? 'text-red-600' : 'text-gray-500'}`} data-id="element-8037">
                        {row.description || 'Missing description'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500" data-id="element-8038">
                        {row.reference || '-'}
                      </td>
                    </tr>;
              })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="flex items-center text-sm text-gray-500 mb-4 py-3 px-4 bg-gray-50 rounded-lg border border-gray-100" data-id="element-8039">
          <CalendarIcon size={14} className="mr-2 text-gray-400 flex-shrink-0" data-id="element-8040" />

          <span data-id="element-8041">
            {t('components.BulkPaymentPreview.payment_links_will_expire_in')}{' '}
            <span className="font-medium text-gray-700" data-id="element-8042">
              {t('components.BulkPaymentPreview.30_days')}
            </span>{' '}
            {t('components.BulkPaymentPreview.from_creation')}
          </span>
        </div>

        <div className="flex justify-between items-center" data-id="element-8043">
          <div data-id="element-8044">
            <p className="text-sm text-gray-600" data-id="element-8045">
              <span className="font-medium" data-id="element-8046">{selectedRows.length}</span>{' '}
              {t('components.BulkPaymentList.of')}{' '}
              <span className="font-medium" data-id="element-8047">{data.length}</span>{' '}
              {t('components.BulkPaymentPreview.payment_requests_selected')}
            </p>
            <p className="text-sm font-medium text-gray-800 mt-1" data-id="element-8048">
              {t('components.BulkPaymentPreview.total')}{' '}
              {formatCurrencyAmount(totalAmount, {
              currency,
              fractionDigits: 2
            })}
            </p>
          </div>
          <div className="flex space-x-4" data-id="element-8049">
            <button type="button" onClick={onBack} className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors" data-id="element-8050">
              {t('components.BulkPaymentPreview.back')}
            </button>
            <button type="button" onClick={onConfirm} disabled={selectedRows.length === 0 || hasErrors || isSubmitting} className={`
                px-4 py-2 font-medium rounded-lg transition-colors
                ${selectedRows.length === 0 || hasErrors || isSubmitting ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-[var(--brand-primary)] hover:bg-[var(--brand-primaryhover)] text-[#1A1A1A]'}
              `} data-id="element-8051">
              {isSubmitting ? 'Creating Batch...' : 'Create Payment Requests'}
            </button>
          </div>
        </div>
      </div>
    </div>;
}